import styled from "styled-components";
import { motion } from "framer-motion";
import { bg, bgMobile } from "../../img";
import { colors } from "../variables";

//main / hero section
export const StyledMain = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: url("${bg}") fixed center no-repeat;
  background-size: cover;
  -webkit-background-size: cover;
  -moz-background-size: cover;
  -o-background-size: cover;
  position: relative;
  color: ${colors.white};
  * {
    transition: all 0.3s;
  }
  /* Keep enough room for the floating cards even on short viewports caused
     by browser zoom or OS display scaling. */
  @media all and (min-width: 1145px) {
    min-height: max(100vh, 56rem);
    padding: clamp(2rem, 5vh, 4rem) 0 clamp(16rem, 28vh, 20rem);
  }
  @media all and (max-width: 967px) {
    background-image: url("${bgMobile}");
    background-position: center top;
    background-attachment: fixed;
  }
`;

export const StyledMainContainer = styled.div`
  width: min(1400px, 92vw);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  .hero-title {
    margin-top: 1rem;
    padding: 1rem 0;
    font-size: 3rem;
    color: ${colors.white};
  }
  .hero-subtitle {
    margin-top: 0.5rem;
    max-width: 55rem;
    color: ${colors.white};
    text-align: center;
    font-size: 1.25rem;
    font-weight: 300;
    line-height: 1.6;
  }
  @media all and (max-width: 1600px) {
    .hero-title {
      font-size: 2.5rem;
    }
  }
  @media all and (max-width: 1144px) {
    width: 90vw;
  }
  @media all and (max-width: 768px) {
    width: 95vw;
    padding: 5rem 0;
    .hero-title {
      font-size: 1.8rem;
    }
    .hero-subtitle {
      font-size: 1.05rem;
    }
  }
`;

//offer section — three service blocks.
//Desktop: floats so its lower half overlaps the consultation section below.
//Mobile (<=960px): a normal stacked section with the cards in a column.
export const StyledOffer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  transform: translateY(50%);
  display: flex;
  justify-content: center;
  z-index: 3;

  @media all and (max-width: 1144px) {
    position: static;
    transform: none;
    width: 100%;
    background: ${colors.lightBg};
    padding: 2.5rem 0;
  }
`;

export const StyledOfferContainer = styled(motion.div)`
  width: 1400px;
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 2.5rem;
  /* vertical padding gives the enlarged middle card room to protrude
     without being clipped by overflow: hidden */
  padding: 1.5rem 0;
  overflow: hidden;
  .card {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2.25rem 1.75rem;
    background: ${colors.dark};
    border-radius: 1rem;
    border-top: 4px solid ${colors.primary};
    color: ${colors.white};
    transition: all 0.3s;
    img {
      width: 72px;
      height: 72px;
      margin-bottom: 1.25rem;
    }
    h3 {
      color: ${colors.primary};
      font-size: 1.25rem;
      font-weight: 600;
      letter-spacing: 1px;
      text-transform: uppercase;
      margin-bottom: 1.25rem;
    }
    p {
      color: ${colors.white};
      text-align: center;
      font-size: 1rem;
      line-height: 1.55;
      flex: 1;
      margin-bottom: 1.25rem;
    }
    .links {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.6rem;
      font-size: 1.1rem;
    }
    a {
      color: ${colors.primary};
      font-weight: 500;
      letter-spacing: 0.5px;
      &:hover {
        color: ${colors.white};
      }
    }
    &:hover {
      transform: translateY(-0.5rem);
      box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.2);
    }
  }
  /* the middle card is enlarged through layout (wider flex share, negative
     vertical margin) so it keeps exactly the same styling as its siblings */
  .card.scaled {
    flex: 1.3;
    margin: -1.5rem 0;
    padding: 3rem 2.25rem;
    img {
      width: 80px;
      height: 80px;
    }
    h3 {
      font-size: 1.7rem;
    }
    p {
      font-size: 1.2rem;
    }
  }

  @media all and (max-width: 1600px) {
    width: 92vw;
    max-width: 1400px;
  }
  @media all and (max-width: 1144px) {
    flex-direction: column;
    align-items: stretch;
    width: 90vw;
    max-width: 600px;
    gap: 1.5rem;
    padding: 0;
    /* cards are stacked: all the same size again */
    .card.scaled {
      flex: 1;
      margin: 0;
      padding: 2.25rem 1.75rem;
    }
  }
  @media all and (max-width: 768px) {
    width: 95vw;
  }
`;

//consultation section
export const StyledConsultation = styled.section`
  padding: 4rem 0;
  background: ${colors.lightBg};
  display: flex;
  align-items: center;
  justify-content: center;
  /* reserve room for the lower half of the floating offer cards */
  @media all and (min-width: 1601px) {
    padding-top: 19rem;
  }
  @media all and (min-width: 1145px) and (max-width: 1600px) {
    padding-top: 20rem;
  }
`;

export const StyledConsultationContainer = styled(motion.div)`
  width: 800px;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  h2 {
    font-weight: 700;
    color: ${colors.primary};
    position: relative;
    margin-bottom: 3rem;
    &:after {
      content: "";
      position: absolute;
      left: 50%;
      transform: translate(-50%, 3rem);
      width: 5rem;
      height: 0.3rem;
      background: ${colors.primary};
    }
  }
  .subtitle {
    font-size: 1.25rem;
    color: ${colors.dark};
    font-weight: 400;
    margin-bottom: 1rem;
  }
  ul {
    list-style: none;
    margin-bottom: 1rem;
    li {
      font-size: 1.1rem;
      color: ${colors.grayText};
      margin-bottom: 0.5rem;
      &:before {
        content: "✔";
        color: ${colors.primary};
        font-weight: bold;
        margin-right: 0.75rem;
      }
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  .note {
    font-size: 1.1rem;
    color: ${colors.dark};
    font-weight: 500;
    margin-bottom: 1rem;
  }
  .consent {
    margin-top: 1rem;
    font-size: 0.95rem;
    color: ${colors.gray};
    a {
      color: ${colors.primary};
      &:hover {
        text-decoration: underline;
      }
    }
  }
  @media all and (max-width: 768px) {
    h2 {
      font-size: 1.5rem;
    }
    .subtitle {
      font-size: 1.1rem;
    }
  }
`;

//principles section
export const StyledPrinciples = styled.section`
  padding: 4rem 0;
  background: ${colors.white};
  display: flex;
  justify-content: center;
`;

export const StyledPrinciplesContainer = styled.div`
  overflow-x: hidden;
  width: 1400px;
  display: flex;
  flex-direction: column;
  color: ${colors.white};
  h2 {
    font-weight: 700;
    color: ${colors.primary};
    position: relative;
    margin-bottom: 3rem;
    &:after {
      content: "";
      position: absolute;
      left: 50%;
      transform: translate(-50%, 3rem);
      width: 5rem;
      height: 0.3rem;
      background: ${colors.primary};
    }
  }
  p {
    color: ${colors.white};
  }

  .intro {
    margin-bottom: 0;
    padding: 0;
    p {
      margin-bottom: 1rem;
    }
    ul {
      margin: 0 0 1rem 1.5rem;
      li {
        color: ${colors.white};
        line-height: 1.7;
        &:before {
          content: "•";
          color: ${colors.primary};
          font-weight: bold;
          display: inline-block;
          width: 1.25em;
          margin-left: -1.25em;
        }
      }
    }
  }

  .principles-content {
    display: grid;
    grid-template-columns: minmax(320px, 0.9fr) minmax(0, 1.1fr);
    gap: clamp(2.5rem, 5vw, 5rem);
    align-items: center;
    margin-top: 5rem;
  }

  .stats {
    width: 100%;
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0;
    background: transparent;
  }

  .stat {
    display: grid;
    grid-template-columns: 8.5rem 1fr;
    align-items: center;
    gap: 1.5rem;
    padding: 1.25rem 0;
    &:first-child {
      padding-top: 0;
    }
    &:last-child {
      padding-bottom: 0;
    }
    strong {
      color: ${colors.primary};
      font-size: clamp(2.5rem, 3.5vw, 3rem);
      font-weight: 800;
      letter-spacing: -0.06em;
      line-height: 0.9;
    }
    span {
      color: ${colors.dark};
      font-size: 1.18rem;
      font-weight: 600;
      line-height: 1.5;
    }
  }

  .story {
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    p {
      color: ${colors.grayText};
      line-height: 1.75;
      margin-bottom: 1rem;
    }
    .lead {
      color: ${colors.dark};
      font-size: 1.2rem;
      font-weight: 600;
    }
    ul {
      display: block;
      list-style: none;
      margin: 0 0 1.25rem;
      padding: 0;
    }
    li,
    .intro & li {
      color: ${colors.grayText};
      line-height: 1.65;
      margin-bottom: 0.35rem;
      padding-left: 0;
      &:before {
        content: "✔";
        color: ${colors.primary};
        display: inline;
        font-weight: bold;
        width: auto;
        margin-left: 0;
        margin-right: 0.75rem;
      }
    }
  }

  .intro .story ul li {
    color: ${colors.dark};
  }

  .intro .story ul li::before {
    content: "✔";
    display: inline;
    position: static;
    width: auto;
    margin-left: 0;
    margin-right: 0.75rem;
  }

  .section-closing-note {
    margin: 2rem auto 0 auto !important;
    color: ${colors.dark};
    font-size: 1.15rem;
    font-weight: 500;
    line-height: 1.7;
    text-align: center;
  }

  .principles {
    display: flex;
    overflow: hidden;
    .first,
    .second,
    .third {
      flex: 1;
      padding: 1rem 2rem;
      text-align: center;
    }
    span {
      width: 50px;
      height: 50px;
      background: ${colors.primary};
      color: ${colors.white};
      line-height: 50px;
      font-weight: 300;
      display: block;
      text-align: center;
      font-size: 20px;
      border-radius: 50%;
      margin: 0 auto;
    }
    h3 {
      font-size: 1.4rem;
      margin: 2rem 0;
    }
  }

  @media all and (max-width: 1600px) {
    width: 1000px;
    .intro {
      margin-bottom: 2rem;
    }
    .principles {
      h3 {
        margin: 1.5rem 0;
      }
    }
  }

  @media all and (max-width: 1144px) {
    width: 90vw;
    .stats {
      width: 100%;
      margin: 0;
    }
  }

  @media all and (max-width: 768px) {
    width: 95vw;
    .intro {
      padding: 0;
    }
    .principles-content {
      grid-template-columns: 1fr;
      gap: 2.5rem;
      margin-top: 4rem;
    }
    .stats {
      width: 100%;
      margin: 0;
    }
    .stat {
      grid-template-columns: 6.5rem 1fr;
      gap: 1rem;
      strong {
        font-size: 2.25rem;
      }
    }
    .principles {
      flex-direction: column;
      .first,
      .second,
      .third {
        padding: 1rem;
      }
    }
  }
`;

export const StyledPrinciplesList = styled.section`
  padding: 4rem 0;
  background: ${colors.lightBg};
  display: flex;
  justify-content: center;
`;

export const StyledPrinciplesListContainer = styled(StyledPrinciplesContainer)`
  h2 {
    text-align: center;
    margin-bottom: 5rem;
  }
  .principles {
    gap: 1.5rem;
    .first,
    .second,
    .third {
      padding: 2.5rem 2rem;
      text-align: left;
      background: ${colors.white};
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-top: 4px solid ${colors.primary};
      border-radius: 0.5rem;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
    }
    span {
      margin: 0 auto;
    }
    h3 {
      color: ${colors.dark};
      margin: 1.75rem 0 1rem;
    }
    p {
      color: ${colors.grayText};
      line-height: 1.7;
    }
  }
`;

//trust / reviews section
export const StyledTrust = styled.section`
  background: ${colors.white};
  padding: 4rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledTrustContainer = styled.div`
  width: 1400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  h2 {
    font-weight: 700;
    color: ${colors.primary};
    position: relative;
    margin-bottom: 3rem;
    &:after {
      content: "";
      position: absolute;
      left: 50%;
      transform: translate(-50%, 3rem);
      width: 5rem;
      height: 0.3rem;
      background: ${colors.primary};
    }
  }
  /* Google reviews widget will be mounted here */
  .reviews-widget {
    width: 100%;
    margin-top: 2rem;
    min-height: 6rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  @media all and (max-width: 1600px) {
    width: 1000px;
  }
  @media all and (max-width: 1144px) {
    width: 90vw;
  }
  @media all and (max-width: 768px) {
    width: 95vw;
    h2 {
      font-size: 1.5rem;
    }
  }
`;
